import dayjs from 'dayjs';

import { NaNToZero } from './number';

export function formatDate(input: dayjs.ConfigType): string {
  return dayjs(input).format('DD/MM/YYYY');
}

/** `DD-MM-YYYY`, the date format used on letters. */
export function formatDateDashed(input: dayjs.ConfigType): string {
  return dayjs(input).format('DD-MM-YYYY');
}

/**
 * Long-form date for letter datelines, e.g. `29 August 2026` / `29 augustus 2026`.
 * Falls back to the dashed format when the locale is not available.
 */
export function formatLongDate(input: dayjs.ConfigType, lang: string): string {
  const date = dayjs(input).toDate();
  try {
    return new Intl.DateTimeFormat(lang.toLowerCase(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Brussels',
    }).format(date);
  } catch {
    return formatDateDashed(date);
  }
}

export function formatGraphQLDate(input: dayjs.ConfigType): string {
  return dayjs(input).format('YYYY-MM-DD');
}

export function formatDateTime(input: dayjs.ConfigType) {
  return dayjs(input).format('DD/MM/YYYY HH:mm');
}

export function formatInputDate(input: dayjs.ConfigType): string {
  return dayjs(input).format('YYYY-MM-DD');
}

export function parseInputTime(input: string): number {
  const parsed = input.split(':');
  if (parsed.length < 2 || parsed[0].startsWith('-') || parsed[1].startsWith('-')) {
    return 0;
  }
  const hours = NaNToZero(+parsed[0]);
  const minutes = NaNToZero(+parsed[1]);
  const result = hours * 60 + minutes;
  return result;
}

export function formatInputTime(minutes: number): string {
  if (minutes <= 0) {
    return '00:00';
  }

  const hours = `${Math.floor(minutes / 60)}`.padStart(2, '0');
  const mins = `${Math.round(minutes % 60)}`.padStart(2, '0');
  return `${hours}:${mins}`;
}

export function formatDateShort(input: dayjs.ConfigType): string {
  return dayjs(input).format('DD/MM');
}

// Calendar-day labels for `YYYY-MM-DD` strings, formatted in UTC so the day never shifts.
const weekdayFormat = new Intl.DateTimeFormat('nl-BE', { weekday: 'long', timeZone: 'UTC' });
const dayMonthFormat = new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** `2026-09-28` → `Maandag` */
export function formatWeekday(date: string): string {
  const weekday = weekdayFormat.format(new Date(`${date}T00:00:00Z`));
  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
}

/** `2026-09-28` → `28 sep` */
export function formatDayMonth(date: string): string {
  return dayMonthFormat.format(new Date(`${date}T00:00:00Z`)).replace('.', '');
}

export function isTimeBeforeOrEqual(startTime: string, endTime: string) {
  try {
    const parsedStart = parseInputTime(startTime);
    const parsedEnd = parseInputTime(endTime);
    return parsedStart <= parsedEnd;
  } catch (err) {
    return true;
  }
}

export function sortDate(a: dayjs.ConfigType, b: dayjs.ConfigType): number {
  return dayjs(a).diff(b);
}

/**
 * "over 1 maand", "in 2 months", … — `Intl` handles the plural rules per
 * language, so this needs no translation keys of its own.
 */
export function formatRelativeToNow(input: dayjs.ConfigType, lang: string): string {
  const target = dayjs(input).startOf('day');
  const today = dayjs().startOf('day');
  const formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'always' });

  const days = target.diff(today, 'day');
  if (Math.abs(days) < 31) {
    return formatter.format(days, 'day');
  }

  const months = target.diff(today, 'month');
  if (Math.abs(months) < 12) {
    return formatter.format(months, 'month');
  }

  return formatter.format(target.diff(today, 'year'), 'year');
}

/**
 * "5 minuten geleden", "3 uur geleden", "gisteren" — how long ago something was, at the coarsest unit
 * that still says anything, so a check that just ran does not read as a count of seconds. `Intl`
 * handles the plural rules per language, so this needs no translation keys of its own.
 */
export function formatTimeSince(input: dayjs.ConfigType, lang: string): string {
  const formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const elapsed = dayjs();

  // Zero seconds is `Intl`'s "now" — the only unit that reads as "zojuist" rather than "binnen een minuut".
  if (elapsed.diff(input, 'second') < 60) {
    return formatter.format(0, 'second');
  }

  const minutes = elapsed.diff(input, 'minute');
  if (minutes < 60) {
    return formatter.format(-minutes, 'minute');
  }

  const hours = elapsed.diff(input, 'hour');
  if (hours < 24) {
    return formatter.format(-hours, 'hour');
  }

  // Whole calendar days, not 24-hour periods: a check from the day before yesterday must not read as
  // "gisteren" just because fewer than 48 hours have passed.
  return formatter.format(dayjs(input).startOf('day').diff(elapsed.startOf('day'), 'day'), 'day');
}
